<template>
  <div class="cloud-task-status" v-if="shouldShowStatus">
    <b-tooltip
      :label="tooltipContent"
      :delay="300"
      position="is-right"
      multilined
      :type="tooltipType"
    >
      <b-icon
        :icon="statusIcon"
        :type="iconType"
        size="is-small"
        :class="iconClass"
        @click.native="refreshStatus"
        class="cloud-task-icon"
      />
    </b-tooltip>
  </div>
</template>

<script>
import { getCloudTaskStatus } from "@/services/pipeline-service";

export default {
  name: "CloudTaskStatus",

  props: {
    taskName: {
      type: String,
      default: null,
    },
    autoRefresh: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      taskStatus: null,
      loading: false,
      error: null,
      lastUpdated: null,
    };
  },

  computed: {
    shouldShowStatus() {
      return !!this.taskName;
    },

    statusIcon() {
      if (this.loading) return "spinner";
      return "cloud";
    },

    iconType() {
      if (this.error) return "is-danger";
      if (this.taskStatus?.error) return "is-danger";
      return "is-white";
    },

    iconClass() {
      return {
        "is-clickable": true,
        "is-spinning": this.loading,
      };
    },

    tooltipContent() {
      if (this.loading) return "🔄 Checking task status...";

      if (!this.taskName) return "No Cloud Task";

      if (this.error) return `❌ Error: ${this.error}`;

      if (!this.taskStatus) return "Click to check task status";

      // Show raw JSON response for debugging
      return `Raw Cloud Task Data:\n${JSON.stringify(
        this.taskStatus,
        null,
        2
      )}`;
    },

    tooltipType() {
      if (this.error) return "is-danger";
      if (this.taskStatus?.error) return "is-danger";
      return "is-white";
    },
  },

  methods: {
    async refreshStatus() {
      if (!this.taskName || this.loading) return;

      this.loading = true;
      this.error = null;

      try {
        this.taskStatus = await getCloudTaskStatus(this.taskName);
        this.lastUpdated = new Date();

        // Emit status change for parent components
        this.$emit("status-updated", this.taskStatus);
      } catch (error) {
        console.error("Error refreshing Cloud Task status:", error);
        this.error = error.message || "Failed to refresh status";
        this.$emit("status-error", error);
      } finally {
        this.loading = false;
      }
    },
  },

  async mounted() {
    if (this.autoRefresh && this.taskName) {
      await this.refreshStatus();
    }
  },

  watch: {
    taskName: {
      handler(newTaskName) {
        if (newTaskName && this.autoRefresh) {
          this.refreshStatus();
        } else if (!newTaskName) {
          this.taskStatus = null;
          this.error = null;
        }
      },
      immediate: false,
    },
  },
};
</script>

<style scoped>
.cloud-task-status {
  display: inline-block;
  position: relative;
}

.cloud-task-icon {
  cursor: pointer;
  transition: all 0.2s ease;
}

.cloud-task-icon:hover {
  transform: scale(1.1);
}

.is-spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
